import config from '../config/environment';

const API_ENDPOINT = config.API_ENDPOINT;

/**
 * Upload Service for handling file uploads via S3 pre-signed URLs
 * This bypasses Lambda payload limits and allows larger file uploads
 */
class S3UploadService {
  /**
   * Generate pre-signed URLs for file uploads
   * @param {string} veteranName - Name of the veteran for folder organization
   * @param {Object} files - Object with file categories as keys and file arrays as values
   * @param {string} formType - Type of form (lineal, memorial, etc.)
   * @returns {Promise<Object>} Pre-signed URLs and file references
   */
  async generatePresignedUrls(veteranName, files, formType = 'unknown') {
    try {
      console.log('Requesting pre-signed URLs for file upload...');
      
      // Prepare file metadata (no file content, just metadata)
      const fileMetadata = {};
      let totalSize = 0;
      
      for (const [category, fileArray] of Object.entries(files)) {
        if (!Array.isArray(fileArray) || fileArray.length === 0) continue;
        
        fileMetadata[category] = fileArray.map(file => {
          // Validate file size is a valid number - handle undefined/null gracefully
          let fileSize = 0;
          if (file && typeof file.size === 'number' && !isNaN(file.size) && file.size >= 0) {
            fileSize = file.size;
          } else if (file && file.size !== undefined) {
            console.warn(`Invalid file size for ${file.name}: ${file.size}, using 0`);
          }
          
          totalSize += fileSize;
          console.log(`File: ${file.name}, Size: ${fileSize} bytes (${(fileSize / (1024 * 1024)).toFixed(2)}MB)`);
          
          return {
            name: file.name,
            type: file.type || 'application/octet-stream',
            size: fileSize
          };
        });
      }
      
      console.log(`Total file size: ${(totalSize / (1024 * 1024)).toFixed(2)}MB`);
      
      const requestPayload = {
        action: 'generate-presigned-urls',
        veteranName: veteranName,
        files: fileMetadata,
        formType: formType
      };
      
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Pre-signed URLs generated successfully');
      
      return result;
      
    } catch (error) {
      console.error('Error generating pre-signed URLs:', error);
      throw new Error(`Failed to generate pre-signed URLs: ${error.message}`);
    }
  }
  
  /**
   * Upload files to S3 using pre-signed URLs
   * @param {Object} files - Object with file categories and actual File objects
   * @param {Object} presignedUrls - Pre-signed URLs from generatePresignedUrls
   * @param {Function} onProgress - Progress callback (optional)
   * @returns {Promise<Object>} Upload results with file references
   */
  async uploadFilesToS3(files, presignedUrls, onProgress = null) {
    try {
      console.log('Starting file uploads to S3...');
      
      const uploadPromises = [];
      const fileReferences = {};
      let completedUploads = 0;
      let totalUploads = 0;
      
      // Count total uploads for progress tracking
      for (const [category, urls] of Object.entries(presignedUrls)) {
        totalUploads += urls.length;
      }
      
      for (const [category, urls] of Object.entries(presignedUrls)) {
        if (!files[category] || !Array.isArray(files[category])) continue;
        
        fileReferences[category] = [];
        
        for (let i = 0; i < urls.length; i++) {
          const urlInfo = urls[i];
          const file = files[category][urlInfo.fileIndex];
          
          if (!file) {
            console.warn(`No file found for ${category}[${urlInfo.fileIndex}]`);
            continue;
          }
          
          // Create upload promise
          const uploadPromise = this.uploadSingleFile(file, urlInfo)
            .then((result) => {
              completedUploads++;
              if (onProgress) {
                onProgress({
                  completed: completedUploads,
                  total: totalUploads,
                  currentFile: file.name,
                  percentage: Math.round((completedUploads / totalUploads) * 100)
                });
              }
              
              // Store file reference for form submission
              fileReferences[category].push({
                name: file.name,
                type: file.type,
                size: typeof file.size === 'number' && !isNaN(file.size) ? file.size : 0,
                s3Key: urlInfo.s3Key,
                s3Path: `s3://${config.S3_BUCKET}/${urlInfo.s3Key}`
              });
              
              return result;
            })
            .catch((error) => {
              console.error(`Failed to upload ${file.name}:`, error);
              throw new Error(`Upload failed for ${file.name}: ${error.message}`);
            });
          
          uploadPromises.push(uploadPromise);
        }
      }
      
      // Wait for all uploads to complete
      await Promise.all(uploadPromises);
      
      console.log(`Successfully uploaded ${completedUploads} files to S3`);
      
      return {
        success: true,
        fileReferences: fileReferences,
        uploadedCount: completedUploads
      };
      
    } catch (error) {
      console.error('Error uploading files to S3:', error);
      throw new Error(`File upload failed: ${error.message}`);
    }
  }
  
  /**
   * Upload a single file to S3 using pre-signed URL
   * @param {File} file - The file to upload
   * @param {Object} urlInfo - Pre-signed URL information
   * @returns {Promise<Object>} Upload result
   */
  async uploadSingleFile(file, urlInfo) {
    try {
      console.log(`Uploading ${file.name} to S3...`);
      console.log(`File details: size=${file.size}, type=${file.type}, lastModified=${file.lastModified}`);
      
      // Validate file size and provide fallback
      const fileSize = typeof file.size === 'number' && !isNaN(file.size) && file.size >= 0 ? file.size : 0;
      
      if (fileSize === 0) {
        console.warn(`Warning: File ${file.name} has zero size, this may cause signature issues`);
      }
      
      const headers = {
        'Content-Type': file.type || 'application/octet-stream',
        'x-amz-server-side-encryption': 'AES256'
      };
      
      // Important: Only add Content-Length if it was included in the signature
      // The pre-signed URL signature must match the actual request headers
      if (fileSize > 0) {
        headers['Content-Length'] = fileSize.toString();
        console.log(`Setting Content-Length header: ${fileSize}`);
      } else {
        console.log(`Skipping Content-Length header for zero-size file: ${file.name}`);
      }
      
      console.log('Request headers:', headers);
      console.log('Pre-signed URL (truncated):', urlInfo.presignedUrl.substring(0, 200) + '...');
      
      const response = await fetch(urlInfo.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: headers
      });
      
      if (!response.ok) {
        // Get detailed error information
        const errorText = await response.text().catch(() => 'Unable to read error response');
        console.error(`Upload failed for ${file.name}:`);
        console.error(`Status: ${response.status} ${response.statusText}`);
        console.error(`Response: ${errorText}`);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      console.log(`Successfully uploaded ${file.name}`);
      
      return {
        fileName: file.name,
        s3Key: urlInfo.s3Key,
        success: true
      };
      
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      throw error;
    }
  }
  
  /**
   * Complete file upload process: generate URLs, upload files, return references
   * @param {string} veteranName - Name of the veteran
   * @param {Object} files - File objects organized by category
   * @param {Function} onProgress - Progress callback (optional)
   * @param {string} formType - Type of form (lineal, memorial, etc.)
   * @returns {Promise<Object>} File references for form submission
   */
  async uploadFiles(veteranName, files, onProgress = null, formType = 'unknown') {
    try {
      console.log('=== Starting upload process ===');
      console.log('Veteran name:', veteranName);
      console.log('Files object:', files);
      
      // Debug: Log each file object structure
      for (const [category, fileArray] of Object.entries(files)) {
        if (Array.isArray(fileArray)) {
          console.log(`Category ${category}:`, fileArray.length, 'files');
          fileArray.forEach((file, index) => {
            console.log(`  File ${index}:`, {
              name: file.name,
              type: file.type,
              size: file.size,
              lastModified: file.lastModified,
              constructor: file.constructor.name
            });
          });
        }
      }
      
      // Step 1: Generate pre-signed URLs
      if (onProgress) {
        onProgress({
          stage: 'generating',
          message: 'Generating upload URLs...',
          percentage: 0
        });
      }
      
      const urlResult = await this.generatePresignedUrls(veteranName, files, formType);
      
      // Step 2: Upload files to S3
      if (onProgress) {
        onProgress({
          stage: 'uploading',
          message: 'Uploading files...',
          percentage: 10
        });
      }
      
      const uploadResult = await this.uploadFilesToS3(files, urlResult.presignedUrls, (progress) => {
        if (onProgress) {
          onProgress({
            stage: 'uploading',
            message: `Uploading ${progress.currentFile}...`,
            percentage: 10 + (progress.percentage * 0.9), // 10-100%
            ...progress
          });
        }
      });
      
      if (onProgress) {
        onProgress({
          stage: 'complete',
          message: 'Upload complete!',
          percentage: 100
        });
      }
      
      return {
        success: true,
        fileReferences: uploadResult.fileReferences,
        uploadInfo: {
          folderName: urlResult.folderName,
          uploadId: urlResult.uploadId,
          uploadedCount: uploadResult.uploadedCount,
          method: 'presigned-url'
        }
      };
      
    } catch (error) {
      console.error('Upload process failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new S3UploadService(); 